import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import {
  Badge,
  Button,
  Card,
  Drawer,
  Field,
  Icon,
  PageHeader,
  Pagination,
  Select,
  Table,
  type Tone,
} from '../../design';
import { useToast } from '../../design/Toast';
import { fmtDateTime, fmtRelative } from '../../lib/format';
import { queryParams, useUrlState } from '../../lib/urlState';
import { useCount, useLockerOptions } from '../operations/common';
import { Kpi } from '../operations/OperationsPage';
import s from '../operations/operations.module.css';
import { ExportButton } from '../reports/ExportButton';

export type Alert = components['schemas']['Alert'];
export type AlertStatus = components['schemas']['AlertStatus'];
export type AlertLevel = components['schemas']['AlertLevel'];
export type AlertType = components['schemas']['AlertType'];
type AlertComment = components['schemas']['AlertComment'];

export const ALERT_STATUS: Record<AlertStatus, { label: string; tone: Tone }> = {
  new: { label: 'Nouă', tone: 'orange' },
  ack: { label: 'În lucru', tone: 'blue' },
  resolved: { label: 'Rezolvată', tone: 'green' },
};
export const ALERT_LEVEL: Record<AlertLevel, { label: string; tone: Tone }> = {
  critical: { label: 'P1 · Critică', tone: 'red' },
  warning: { label: 'P2 · Avertizare', tone: 'orange' },
};
export const ALERT_TYPE: Record<AlertType, string> = {
  offline: 'Locker offline',
  door_open: 'Ușă deschisă',
  door_timeout: 'Ușă deschisă peste prag',
  board_unavailable: 'Placă indisponibilă',
  access_attempts: 'Încercări de acces eșuate',
  capacity: 'Capacitate aproape epuizată',
  document_retention: 'Document la termenul de retenție',
};

function count(q: { status?: AlertStatus; level?: AlertLevel }) {
  return () =>
    unwrap(api.GET('/alerts', { params: { query: { ...q, pageSize: 1 } } })) as Promise<{
      total: number;
    }>;
}

export function AlertsPage() {
  const { user, has } = useAuth();
  const { values, page, pageSize, set } = useUrlState([
    'status',
    'level',
    'type',
    'lockerId',
    'assigneeId',
  ] as const);
  const lockers = useLockerOptions();
  const lockerName = (id: string) => lockers.data?.items.find((l) => l.id === id)?.name ?? '…';
  const [openId, setOpenId] = useState<string | null>(null);

  const critNew = useCount(
    ['alerts', 'critical', 'new'],
    count({ level: 'critical', status: 'new' }),
  );
  const critAck = useCount(
    ['alerts', 'critical', 'ack'],
    count({ level: 'critical', status: 'ack' }),
  );
  const warnNew = useCount(
    ['alerts', 'warning', 'new'],
    count({ level: 'warning', status: 'new' }),
  );
  const warnAck = useCount(
    ['alerts', 'warning', 'ack'],
    count({ level: 'warning', status: 'ack' }),
  );
  const resolved = useCount(['alerts', 'resolved'], count({ status: 'resolved' }));
  const sum = (...xs: (number | undefined)[]) =>
    xs.some((x) => x === undefined) ? undefined : xs.reduce((a, b) => (a ?? 0) + (b ?? 0), 0);

  const query = useQuery({
    queryKey: ['alerts', { ...values, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/alerts', {
          params: {
            query: queryParams({
              status: (values.status || undefined) as AlertStatus | undefined,
              level: (values.level || undefined) as AlertLevel | undefined,
              type: (values.type || undefined) as AlertType | undefined,
              lockerId: values.lockerId || undefined,
              assigneeId: values.assigneeId || undefined,
              page,
              pageSize,
            }),
          },
        }),
      ),
    refetchInterval: 30_000,
  });

  return (
    <>
      <PageHeader
        title="Alerte & incidente"
        subtitle="Detecție, preluare, intervenție și confirmarea revenirii la normal."
        actions={
          <ExportButton
            kind="alerts"
            params={{
              status: values.status || undefined,
              level: values.level || undefined,
              type: values.type || undefined,
              lockerId: values.lockerId || undefined,
              assigneeId: values.assigneeId || undefined,
            }}
          />
        }
      />
      <div className={s.kpis}>
        <Kpi
          label="Critice active"
          value={sum(critNew.total, critAck.total)}
          hint="Necesită intervenție prioritară"
          tone="red"
        />
        <Kpi
          label="Avertizări active"
          value={sum(warnNew.total, warnAck.total)}
          hint="Uși, plăci, capacitate sau comunicație"
          tone="orange"
        />
        <Kpi
          label="Preluate, în lucru"
          value={sum(critAck.total, warnAck.total)}
          hint="Preluarea nu înseamnă rezolvare"
        />
        <Kpi
          label="Rezolvate"
          value={resolved.total}
          hint="Manual sau automat, la revenirea condiției"
          tone="green"
        />
      </div>
      <Card tight>
        <div className="flex wrap" style={{ padding: 16 }}>
          <Select
            value={values.status}
            onChange={(e) => set({ status: e.target.value })}
            style={{ maxWidth: 180 }}
            aria-label="Stare"
          >
            <option value="">Toate stările</option>
            {(Object.keys(ALERT_STATUS) as AlertStatus[]).map((st) => (
              <option key={st} value={st}>
                {ALERT_STATUS[st].label}
              </option>
            ))}
          </Select>
          <Select
            value={values.level}
            onChange={(e) => set({ level: e.target.value })}
            style={{ maxWidth: 200 }}
            aria-label="Severitate"
          >
            <option value="">Toate severitățile</option>
            {(Object.keys(ALERT_LEVEL) as AlertLevel[]).map((lv) => (
              <option key={lv} value={lv}>
                {ALERT_LEVEL[lv].label}
              </option>
            ))}
          </Select>
          <Select
            value={values.type}
            onChange={(e) => set({ type: e.target.value })}
            style={{ maxWidth: 260 }}
            aria-label="Tip"
          >
            <option value="">Toate tipurile</option>
            {(Object.keys(ALERT_TYPE) as AlertType[]).map((t) => (
              <option key={t} value={t}>
                {ALERT_TYPE[t]}
              </option>
            ))}
          </Select>
          <Select
            value={values.lockerId}
            onChange={(e) => set({ lockerId: e.target.value })}
            style={{ maxWidth: 240 }}
            aria-label="Locker"
          >
            <option value="">Toate lockerele</option>
            {lockers.data?.items.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
          <label className="flex small">
            <input
              type="checkbox"
              checked={values.assigneeId === user?.id}
              onChange={(e) => set({ assigneeId: e.target.checked ? user?.id : '' })}
            />
            Doar ale mele
          </label>
        </div>
        <Table<Alert>
          columns={[
            {
              key: 'level',
              header: 'Severitate',
              render: (a) => (
                <Badge tone={ALERT_LEVEL[a.level].tone}>{ALERT_LEVEL[a.level].label}</Badge>
              ),
            },
            {
              key: 'title',
              header: 'Incident',
              render: (a) => (
                <>
                  <strong>{a.title}</strong>
                  <div className="small muted">{ALERT_TYPE[a.type]}</div>
                </>
              ),
            },
            { key: 'locker', header: 'Locker', render: (a) => lockerName(a.lockerId) },
            {
              key: 'since',
              header: 'De când',
              render: (a) => (
                <>
                  {fmtRelative(a.createdAt)}
                  <div className="small muted">{fmtDateTime(a.createdAt)}</div>
                </>
              ),
            },
            {
              key: 'owner',
              header: 'Responsabil',
              render: (a) => a.assigneeLabel ?? <span className="muted">—</span>,
            },
            {
              key: 'status',
              header: 'Stare',
              render: (a) => (
                <Badge tone={ALERT_STATUS[a.status].tone}>{ALERT_STATUS[a.status].label}</Badge>
              ),
            },
          ]}
          rows={query.data?.items ?? []}
          rowKey={(a) => a.id}
          onRowClick={(a) => setOpenId(a.id)}
          empty={
            query.isLoading
              ? 'Se încarcă…'
              : query.error
                ? errorMessage(query.error)
                : 'Nicio alertă pentru filtrele alese.'
          }
          footer={
            query.data && (
              <Pagination
                page={query.data.page}
                pageSize={query.data.pageSize}
                total={query.data.total}
                onPage={(p) => set({ page: p })}
              />
            )
          }
        />
      </Card>
      <p className="small muted" style={{ marginTop: 12 }}>
        Ușa închisă și încuiată este starea normală, nu o alertă. Sunt semnalate lockerul offline,
        placa indisponibilă, ușa deschisă peste prag, încercările repetate de acces eșuate și
        capacitatea aproape epuizată. Notificările pe e-mail / SMS se configurează în Automatizări.
      </p>
      {openId && (
        <AlertDrawer id={openId} onClose={() => setOpenId(null)} canResolve={has('resolve')} />
      )}
    </>
  );
}

export function AlertDrawer({
  id,
  onClose,
  canResolve,
}: {
  id: string;
  onClose: () => void;
  canResolve: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [comment, setComment] = useState('');
  const query = useQuery({
    queryKey: ['alert', id],
    queryFn: () => unwrap(api.GET('/alerts/{id}', { params: { path: { id } } })),
  });
  const comments = useQuery({
    queryKey: ['alert', id, 'comments'],
    queryFn: () => unwrap(api.GET('/alerts/{id}/comments', { params: { path: { id } } })),
  });
  const assignees = useQuery({
    queryKey: ['alert', id, 'assignees'],
    queryFn: () => unwrap(api.GET('/alerts/{id}/assignees', { params: { path: { id } } })),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['alert', id] });
    void qc.invalidateQueries({ queryKey: ['alerts'] });
    void qc.invalidateQueries({ queryKey: ['count', 'alerts'] });
  };
  const ack = useMutation({
    mutationFn: () => unwrap(api.POST('/alerts/{id}/ack', { params: { path: { id } } })),
    onSuccess: () => {
      toast('Alerta a fost preluată.', 'success');
      refresh();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const resolve = useMutation({
    mutationFn: () => unwrap(api.POST('/alerts/{id}/resolve', { params: { path: { id } } })),
    onSuccess: () => {
      toast('Alerta a fost rezolvată.', 'success');
      refresh();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const assign = useMutation({
    mutationFn: (assigneeId: string | null) =>
      unwrap(api.PATCH('/alerts/{id}', { params: { path: { id } }, body: { assigneeId } })),
    onSuccess: () => {
      toast('Responsabilul a fost actualizat.', 'success');
      refresh();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const addComment = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/alerts/{id}/comments', {
          params: { path: { id } },
          body: { text: comment.trim() },
        }),
      ),
    onSuccess: () => {
      setComment('');
      void qc.invalidateQueries({ queryKey: ['alert', id, 'comments'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const a = query.data;
  return (
    <Drawer
      title={a?.title ?? 'Alertă'}
      onClose={onClose}
      footer={
        a && (
          <>
            {a.status === 'new' && (
              <Button onClick={() => ack.mutate()} busy={ack.isPending}>
                Preia
              </Button>
            )}
            {canResolve && a.status !== 'resolved' && (
              <Button
                variant="primary"
                icon="check"
                onClick={() => resolve.mutate()}
                busy={resolve.isPending}
              >
                Rezolvă
              </Button>
            )}
            <Button onClick={onClose}>Închide</Button>
          </>
        )
      }
    >
      {!a && <p className="muted">{query.error ? errorMessage(query.error) : 'Se încarcă…'}</p>}
      {a && (
        <div className="stack">
          <div className="flex between">
            <Badge tone={ALERT_LEVEL[a.level].tone}>{ALERT_LEVEL[a.level].label}</Badge>
            <Badge tone={ALERT_STATUS[a.status].tone}>{ALERT_STATUS[a.status].label}</Badge>
          </div>
          <div
            className={s.warn}
            role="note"
            style={
              a.level === 'critical'
                ? { background: '#fbe6e7', borderColor: '#f0b4b7', color: '#7a1f24' }
                : undefined
            }
          >
            <Icon name="alert" size={18} />
            <span>{a.description || ALERT_TYPE[a.type]}</span>
          </div>
          <dl className={s.pairs}>
            <dt>Tip</dt>
            <dd>{ALERT_TYPE[a.type]}</dd>
            <dt>Locker</dt>
            <dd>
              <Link to={`/lockere/${a.lockerId}`}>Deschide lockerul</Link>
            </dd>
            <dt>Detectată</dt>
            <dd>{fmtDateTime(a.createdAt)}</dd>
            {a.ackedAt && (
              <>
                <dt>Preluată</dt>
                <dd>
                  {fmtDateTime(a.ackedAt)} · {a.ackedBy}
                </dd>
              </>
            )}
            {a.resolvedAt && (
              <>
                <dt>Rezolvată</dt>
                <dd>
                  {fmtDateTime(a.resolvedAt)} · {a.resolvedBy ?? 'sistem'}
                </dd>
              </>
            )}
          </dl>
          <Field label="Responsabil" htmlFor="alert-assignee">
            <Select
              id="alert-assignee"
              value={a.assigneeId ?? ''}
              disabled={a.status === 'resolved' || assign.isPending}
              onChange={(e) => assign.mutate(e.target.value || null)}
            >
              <option value="">— fără responsabil —</option>
              {(assignees.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email}
                  {u.global ? ' · platformă' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <div>
            <h3>Comentarii de intervenție</h3>
            <div className="stack" style={{ marginTop: 8 }}>
              {(comments.data ?? []).map((c: AlertComment) => (
                <div key={c.id} style={{ borderLeft: '3px solid var(--line)', paddingLeft: 10 }}>
                  <div className="small">
                    <strong>{c.authorLabel}</strong>{' '}
                    <span className="muted">· {fmtDateTime(c.createdAt)}</span>
                  </div>
                  <div className="small">{c.text}</div>
                </div>
              ))}
              {comments.data && comments.data.length === 0 && (
                <p className="small muted">Niciun comentariu.</p>
              )}
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder="Ce ai verificat / cu cine ai vorbit…"
                aria-label="Comentariu nou"
                style={{
                  width: '100%',
                  border: '1px solid var(--line)',
                  borderRadius: 9,
                  padding: 8,
                  font: 'inherit',
                }}
              />
              <div>
                <Button
                  small
                  onClick={() => addComment.mutate()}
                  disabled={!comment.trim()}
                  busy={addComment.isPending}
                >
                  Adaugă comentariu
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}
